import { useEffect } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { HiX } from "react-icons/hi";

const FullscreenMediaViewer = ({
    open,
    src,
    type = "image",
    onClose,
    hint = "Click anywhere to close",
}) => {
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, [open]);

    if (typeof document === "undefined") return null;

    return ReactDOM.createPortal(
        <AnimatePresence>
            {open && !!src && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[500000000] flex items-center justify-center p-4"
                    onClick={onClose}
                    role="dialog"
                    aria-modal="true"
                >
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 bg-black/95 backdrop-blur-xl"
                    />
                    <motion.button
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        whileHover={{ scale: 1.1, rotate: 90 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={onClose}
                        aria-label="Close"
                        className="absolute top-6 right-6 z-[1] p-4 bg-gray-200/80 dark:bg-white/10 hover:bg-gray-300/90 dark:hover:bg-white/20 backdrop-blur-xl rounded-full text-gray-900 dark:text-white shadow-2xl border border-gray-300 dark:border-white/20 transition-colors"
                    >
                        <HiX className="text-3xl" />
                    </motion.button>
                    <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.8, opacity: 0 }}
                        onClick={(e) => e.stopPropagation()}
                        className="relative max-w-[100vw] max-h-[100vh]"
                    >
                        {(type === "image" || type === "gif") && (
                            <img
                                src={src}
                                alt="Media full view"
                                className="max-w-full max-h-[100vh] rounded-2xl shadow-2xl object-contain"
                            />
                        )}
                        {type === "video" && (
                            <video
                                src={src}
                                preload="metadata"
                                autoPlay={false}
                                controls
                                className="max-w-full max-h-[100vh] rounded-2xl shadow-2xl object-contain"
                            />
                        )}
                    </motion.div>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        transition={{ delay: 0.2 }}
                        className="absolute bottom-8 text-gray-600 dark:text-white/60 text-sm pointer-events-none"
                    >
                        {hint}
                    </motion.p>
                </motion.div>
            )}
        </AnimatePresence>,
        document.body
    );
};

export default FullscreenMediaViewer;
